import React from 'react'
import PropTypes from 'prop-types'
import { isFlagOn, K12_CORE_CURRICULUM_SERVICE_FLAG } from 'utils/featureFlags'
import { isCurriculumServiceProductPath } from 'utils/routing/routeHelpers'
import {
  RouteContentGetter,
  RouteContentType,
} from 'layers/content/v2/Hocs/RouteContentGetter'
import { CurriculumRouteContentGetter } from 'layers/content/v3/Hocs/CurriculumRouteContentGetter'

export { RouteContentType }

function defaultRouteResolver(match) {
  if (match?.url) return match.url
  if (typeof window !== 'undefined') return window?.location?.pathname || ''
  return ''
}

/**
 * Flag-switched HOC factory. Returns a component that checks the flag
 * at render time, delegating to v3 CurriculumRouteContentGetter when on
 * or the original v2 RouteContentGetter when off.
 *
 * Drop-in replacement for RouteContentGetter — same signature.
 *
 * Curriculum-service (v3) is only used for K12 product URLs: digital
 * well-being, elementary, middle-school, and high-school. Everything else
 * that flows through this HOC stays on route-service (v2). Contentful (v1)
 * PagesContainer routes are unchanged.
 */
export function FlaggedRouteContentGetter(Component, options) {
  const CurriculumWrapped = CurriculumRouteContentGetter(Component, options)
  const LegacyWrapped = RouteContentGetter(Component, options)
  const { routeResolver = defaultRouteResolver } = options || {}

  function FlaggedComponent(props) {
    const route = routeResolver(props.match)
    const useCurriculumService =
      isFlagOn([K12_CORE_CURRICULUM_SERVICE_FLAG]) &&
      isCurriculumServiceProductPath(route)

    if (useCurriculumService) {
      return <CurriculumWrapped {...props} />
    }
    return <LegacyWrapped {...props} />
  }

  FlaggedComponent.propTypes = {
    match: PropTypes.shape({
      url: PropTypes.string,
    }),
  }

  FlaggedComponent.displayName = `FlaggedRouteContentGetter(${Component.displayName ||
    Component.name ||
    'Component'})`

  return FlaggedComponent
}
